import TableHeader from '@/components/table/tableHeader';
import { useResponsive } from '@/hooks/use-responsive';
import styled from 'styled-components';

import {
  CommitmentTransactionType,
  InvestorDetailsActivityTabListEntityTransactionsQuery,
  ListEntityCommitmentTransactionSortBy
} from '@/__generated__/graphql';
import { IFilterBarData } from '@/components/basicComponents/filterBar';
import { Loader } from '@/components/loaders/loader';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import { TableBodyWrapper, TableWrapper } from '@/components/table/styles';
import TablePagination from '@/components/table/tablePagination';
import { SORT_ORDER } from '@/components/table/types';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { InvestorDetailsEntityTypeEnum } from '../../../types';
import { activityMobileTableSort, activityTabFilterBarItems, activityTableColumns } from '../constants';
import { LIST_ENTITY_TRANSACTIONS_QUERY } from '../queries';
import ActivityMobileTable from './mobileTable';
import ActivityTableBody from './tableBody';

const getTransactionTypeFilterItems = (filterBar: IFilterBarData[]) => {
  const filterBarEnabledValues = filterBar.filter((item) => item.enabled).map((item) => item.value);

  if (!filterBarEnabledValues.length) return null;
  return filterBarEnabledValues as CommitmentTransactionType[];
};

interface ActivityTableProps {
  entityType: InvestorDetailsEntityTypeEnum;
  entityIdsForActivityTab: string[];
}

const ActivityTable = ({ entityType, entityIdsForActivityTab }: ActivityTableProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const {
    searchParams,
    onChangePaginationData,
    onChangeFilterBar,
    onChangeSearch,
    onChangeSort,
    onChangePrimaryFilter,
    makeScroll,
    saveScrollPostion
  } = useSearchParamsManager();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';
  const q = searchParams.get('q') || '';
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const key = (searchParams.get('key') || 'DATE') as ListEntityCommitmentTransactionSortBy;
  const asc = searchParams.get('asc') === 'true' || false;
  const primaryFilter = searchParams.get('primaryFilter') || '';
  const filterBarParams = searchParams.get('filterBar');
  const position = searchParams.get('position') || 0;

  const [tableRows, setTableRows] = useState<InvestorDetailsActivityTabListEntityTransactionsQuery['ListEntityTransactions']['data']>([]);
  const [filterBar, setFilterBar] = useState<IFilterBarData[]>(activityTabFilterBarItems);
  const [primaryFilterItems, setPrimaryFilterItems] = useState<
    InvestorDetailsActivityTabListEntityTransactionsQuery['ListEntityTransactions']['investments']
  >([]);
  const [mobileSort, setMobileSort] = useState(activityMobileTableSort[2].title);
  const sortItem = activityMobileTableSort.find((item) => item.title === mobileSort);
  const mobileSortList = activityMobileTableSort.map((item) => item.title);

  const {
    data: listEntityTransactionsData,
    loading: listEntityTransactionsLoading,
    refetch: listEntityTransactionsRefetch
  } = useQuery(LIST_ENTITY_TRANSACTIONS_QUERY, {
    skip: !entityIdsForActivityTab?.length,
    notifyOnNetworkStatusChange: true,
    variables: {
      legalEntityIds: entityIdsForActivityTab,
      page: Math.floor(Number(offset) / Number(limit)) + 1,
      rowsPerPage: Number(limit),
      sortBy: key,
      order: asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      search: q.toLowerCase().trim(),
      transactionTypeFilter: getTransactionTypeFilterItems(filterBar),
      investmentId: !!primaryFilter ? primaryFilter : undefined
    },
    onCompleted: (data) => {
      setFilterBar(
        filterBar.map((item) => ({ ...item, count: data.ListEntityTransactions.filters.find((filter) => filter.value === item.value)?.count ?? 0 }))
      );

      setTableRows(data.ListEntityTransactions.data);
      setPrimaryFilterItems(data.ListEntityTransactions.investments.sort((a, b) => a.name.localeCompare(b.name)));
      makeScroll(position);
    }
  });

  useEffect(() => {
    if (!filterBarParams) return;
    const matchedFilterBarValues = activityTabFilterBarItems.map((item) => {
      const matchedFilterItem = JSON.parse(filterBarParams).find((filterItem: string) => filterItem === item.value);
      return {
        ...item,
        enabled: Boolean(matchedFilterItem)
      };
    });
    setFilterBar(matchedFilterBarValues);
  }, []);

  useEffect(() => {
    if (!isMobile) return;

    listEntityTransactionsRefetch({
      order: sortItem?.asc ? SORT_ORDER.ASC : SORT_ORDER.DESC,
      sortBy: sortItem?.key as ListEntityCommitmentTransactionSortBy
    });
  }, [mobileSort]);

  const openOpportunityDetailsPage = (strategyId: string) => {
    saveScrollPostion();
    navigate(`/strategyDetails/${strategyId}?backToTitle=Investor Details`);
  };

  return (
    <>
      <FilterPage
        search={{
          value: q,
          onChange: onChangeSearch,
          placeholder: 'Search'
        }}
        resultsValue={listEntityTransactionsData?.ListEntityTransactions?.total || 0}
        refetch={listEntityTransactionsRefetch}
        filterBar={filterBar}
        onChangeFilterBar={onChangeFilterBar}
        quickFilter={{
          title: 'Strategy',
          defaultValue: 'All',
          value: primaryFilter,
          onChange: onChangePrimaryFilter,
          options: [{ label: 'All', value: '' }, ...primaryFilterItems.map((item) => ({ label: item.name, value: item.id }))]
        }}
        mobileSort={{
          value: mobileSort,
          onChange: setMobileSort,
          selects: mobileSortList
        }}
        useNewFilters
      />
      {isMobile ? (
        <>
          {listEntityTransactionsLoading ? (
            <Loader />
          ) : (
            <ActivityMobileTable tableRows={tableRows} entityType={entityType} openOpportunityDetailsPage={openOpportunityDetailsPage} />
          )}
        </>
      ) : (
        <TableWrapper padding="0">
          <TableHeader refetch={() => {}} columns={activityTableColumns} savedSort={{ key, asc }} savedSetSort={onChangeSort} />
          <TableBodyWrapper>
            {listEntityTransactionsLoading ? (
              <Loader />
            ) : (
              <ActivityTableBody tableRows={tableRows} entityType={entityType} openOpportunityDetailsPage={openOpportunityDetailsPage} />
            )}
          </TableBodyWrapper>
        </TableWrapper>
      )}
      <PaginationWrap>
        <TablePagination
          savePagination={onChangePaginationData}
          paginationValues={{
            limit: Number(limit),
            offset: Number(offset),
            total: listEntityTransactionsData?.ListEntityTransactions?.total ?? 0
          }}
          refetch={() => null as any}
        />
      </PaginationWrap>
    </>
  );
};

export default ActivityTable;

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
